import { useState, useMemo, useEffect } from 'react';
import { Plus, Pencil, X, CheckSquare } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { warrantyApi } from '../api/warrantyApi';
import { usePartsTree } from '../hooks/useParts';
import { CategorySection } from './CategorySection';
import { transformPartsTreeToCategories } from '../utils/transformPartsTree';
import { FormInput } from '@/Components/shared/FormInput';
import { FormSelect } from '@/Components/shared/FormSelect';
import type { Warranty, CreateWarrantyDto } from '../types/warranty.types';
import type { VendorWording } from '@/utils/vendorWording';
import { useAllowedDurations } from '../hooks/useAllowedDurations';
import { useWarrantyPermissions } from '../hooks/useWarrantyPermissions';
import { useCanMonthlyPayment } from '@/features/societies/hooks/useCanMonthlyPayment';
import { useNotifications } from '@/contexts/NotificationContext';
import { Checkbox } from '@/Components/ui/Checkbox';

interface CreateCustomWarrantyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (warranty: Warranty, partIds?: number[]) => void;
  societyId: string;
  vehicleType?: number;
  wording: VendorWording;
  editWarranty?: Warranty | null;
  editPartIds?: number[];
  allowedVehicleTypes?: number[];
  autoWarrantyType?: 0 | 1;
}

const DURATION_OPTIONS = [
  { value: 3, label: '3 mois' },
  { value: 6, label: '6 mois' },
  { value: 12, label: '12 mois' },
  { value: 24, label: '24 mois' },
  { value: 36, label: '36 mois' },
  { value: 48, label: '48 mois' },
  { value: 60, label: '60 mois' },
];

const VEHICLE_TYPE_OPTIONS = [
  { value: 0, label: 'Standard' },
  { value: 1, label: 'Collection' },
  { value: 2, label: 'Deux-roues' },
];

const WARRANTY_TYPE_OPTIONS = [
  { value: 0, label: 'Garantie' },
  { value: 1, label: 'Prestation' },
];

export const CreateCustomWarrantyModal = ({
  isOpen,
  onClose,
  onCreated,
  societyId,
  vehicleType: initialVehicleType,
  wording,
  editWarranty,
  editPartIds,
  allowedVehicleTypes,
  autoWarrantyType,
}: CreateCustomWarrantyModalProps) => {
  const queryClient = useQueryClient();
  const isEditMode = !!editWarranty;
  const { showNotification } = useNotifications();
  const permissions = useWarrantyPermissions(societyId);
  const canMonthlyPayment = useCanMonthlyPayment(societyId);

  const [form, setForm] = useState({
    name: '',
    warrantyType: autoWarrantyType ?? 0,
    vehicleType: initialVehicleType ?? 0,
    duration: '' as string | number,
    financialCap: '' as string | number,
    kmLimit: '' as string | number,
    sellingPrice: '' as string | number,
    isCashPayment: true,
    isMensualisable: false,
  });

  const filteredVehicleTypeOptions = useMemo(() => {
    if (!allowedVehicleTypes || allowedVehicleTypes.length === 0) return VEHICLE_TYPE_OPTIONS;
    return VEHICLE_TYPE_OPTIONS.filter(o => allowedVehicleTypes.includes(o.value));
  }, [allowedVehicleTypes]);

  const allowedDurations = useAllowedDurations(societyId);
  const filteredDurationOptions = useMemo(() => {
    return DURATION_OPTIONS.filter(o => allowedDurations.includes(o.value as any));
  }, [allowedDurations]);

  const [selectedPartIds, setSelectedPartIds] = useState<number[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  const { data: partsTree, isLoading: isLoadingParts } = usePartsTree('fr', form.vehicleType);

  const categories = useMemo(() => {
    return transformPartsTreeToCategories(partsTree);
  }, [partsTree]);

  // Initialiser le formulaire en mode édition quand les catégories sont chargées
  useEffect(() => {
    if (!isOpen || initialized) return;

    if (editWarranty && editPartIds && categories.length > 0) {
      setForm({
        name: editWarranty.name,
        warrantyType: editWarranty.warrantyType as 0 | 1,
        vehicleType: editWarranty.vehicleType,
        duration: editWarranty.duration,
        financialCap: editWarranty.financialCap,
        kmLimit: editWarranty.kmLimit ?? '',
        sellingPrice: editWarranty.sellingPrice,
        isCashPayment: editWarranty.isCashPayment,
        isMensualisable: editWarranty.isMensualisable,
      });
      setSelectedPartIds(editPartIds);

      // Ouvrir les catégories qui contiennent des pièces sélectionnées
      const editSet = new Set(editPartIds);
      const expanded = new Set<string>();
      for (const cat of categories) {
        if (cat.parts.some((p: any) => editSet.has(p.id))) {
          expanded.add(cat.catKey);
        }
      }
      setExpandedCategories(expanded);
      setInitialized(true);
    } else if (!editWarranty) {
      setInitialized(true);
    }
  }, [isOpen, editWarranty, editPartIds, categories, initialized]);

  // Auto-reset durée si plus disponible dans la config
  useEffect(() => {
    if (form.duration && !allowedDurations.includes(Number(form.duration) as any)) {
      setForm(prev => ({ ...prev, duration: '' }));
    }
  }, [allowedDurations]);

  // Réinitialiser quand la modale se ferme
  useEffect(() => {
    if (!isOpen) {
      setInitialized(false);
      setError(null);
      setFieldErrors({});
      if (!editWarranty) {
        setForm({
          name: '',
          warrantyType: autoWarrantyType ?? 0,
          vehicleType: initialVehicleType ?? 0,
          duration: '',
          financialCap: '',
          kmLimit: '',
          sellingPrice: '',
          isCashPayment: true,
          isMensualisable: false,
        });
        setSelectedPartIds([]);
        setExpandedCategories(new Set());
      }
    }
  }, [isOpen]);

  const clearFieldError = (key: string) => {
    if (fieldErrors[key]) {
      setFieldErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const handleCategoryToggle = (categoryKey: string) => {
    const category = categories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const isCurrentlyExpanded = expandedCategories.has(categoryKey);
    const newExpanded = new Set(expandedCategories);

    if (isCurrentlyExpanded) {
      newExpanded.delete(categoryKey);
      const partIdsToRemove = category.parts.map(p => p.id);
      setSelectedPartIds(prev => prev.filter(id => !partIdsToRemove.includes(id)));
    } else {
      newExpanded.add(categoryKey);
      const priority1Ids = category.parts.filter(p => p.selectionPriority === 1).map(p => p.id);
      setSelectedPartIds(prev => [...new Set([...prev, ...priority1Ids])]);
      clearFieldError('parts');
    }

    setExpandedCategories(newExpanded);
  };

  const handlePartToggle = (partId: number, priority: number) => {
    const isSelected = selectedPartIds.includes(partId);
    if (isSelected) {
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      setSelectedPartIds(prev => prev.filter(id => id !== partId));
    } else {
      setSelectedPartIds(prev => [...prev, partId]);
      clearFieldError('parts');
    }
  };

  const handleSelectAllPriority = (categoryKey: string, priority: number) => {
    const category = categories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const priorityIds = category.parts.filter(p => p.selectionPriority === priority).map(p => p.id);
    const allSelected = priorityIds.every(id => selectedPartIds.includes(id));

    if (allSelected) {
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      setSelectedPartIds(prev => prev.filter(id => !priorityIds.includes(id)));
    } else {
      setSelectedPartIds(prev => [...new Set([...prev, ...priorityIds])]);
    }
  };

  const handleSelectAll = () => {
    const allPartIds = categories.flatMap(cat => cat.parts.map(p => p.id));
    const allSelected = allPartIds.every(id => selectedPartIds.includes(id));

    if (allSelected) {
      setSelectedPartIds([]);
      setExpandedCategories(new Set());
    } else {
      setSelectedPartIds(allPartIds);
      setExpandedCategories(new Set(categories.map(cat => cat.catKey)));
    }
  };

  const allSelected = useMemo(() => {
    const allPartIds = categories.flatMap(cat => cat.parts.map(p => p.id));
    return allPartIds.length > 0 && allPartIds.every(id => selectedPartIds.includes(id));
  }, [categories, selectedPartIds]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateWarrantyDto) => warrantyApi.createWarranty(dto),
    onSuccess: (warranty) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.setQueryData(['warranty', warranty.uuid], warranty);
      onCreated(warranty, selectedPartIds);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err.message || 'Erreur lors de la création');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, data }: { uuid: string; data: Record<string, any> }) =>
      warrantyApi.updateWarranty(uuid, data),
    onSuccess: (warranty) => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.setQueryData(['warranty', warranty.uuid], warranty);
      onCreated(warranty, selectedPartIds);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err.message || 'Erreur lors de la modification');
    },
  });

  const mutation = isEditMode ? updateMutation : createMutation;

  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
    clearFieldError(key);
  };

  // Sans mensualisation sur le compte, seul le comptant est possible, quel que soit
  // l'état du formulaire (ex : garantie sur mesure déjà mensualisée en mode édition)
  const isMensualisable = canMonthlyPayment && form.isMensualisable;
  const isCashPayment = !canMonthlyPayment || form.isCashPayment;

  // Vérifier si le prix total est trop bas pour la mensualisation (minimum 200€)
  const isMensualTooLow = useMemo(() => {
    if (!isMensualisable) return false;
    const price = Number(form.sellingPrice);
    if (!price || price <= 0) return false;
    const isMonthlyOnly = isMensualisable && !isCashPayment;
    const totalPrice = isMonthlyOnly ? price * (Number(form.duration) || 0) : price;
    return totalPrice < 200;
  }, [isMensualisable, isCashPayment, form.sellingPrice, form.duration]);

  const handleSubmit = () => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Le nom est requis';
    if (!form.duration) errors.duration = 'La durée est requise';
    if (form.financialCap === '' || form.financialCap === null || Number(form.financialCap) < 1) errors.financialCap = 'Le plafond financier est requis';
    if (form.sellingPrice === '' || form.sellingPrice === null || Number(form.sellingPrice) < 0) errors.sellingPrice = 'Le prix de vente est requis';
    if (isMensualTooLow) errors.sellingPrice = 'La mensualisation n\'est pas disponible pour un prix total inférieur à 200€';
    if (!isCashPayment && !isMensualisable) errors.paymentMode = 'Sélectionnez au moins un mode de paiement';
    if (!form.kmLimit || Number(form.kmLimit) <= 0) errors.kmLimit = 'Le plafond km est requis';
    if (selectedPartIds.length === 0) errors.parts = 'Veuillez sélectionner au moins une pièce';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    if (isEditMode && editWarranty) {
      updateMutation.mutate({
        uuid: editWarranty.uuid,
        data: {
          name: form.name.trim(),
          warrantyType: form.warrantyType,
          vehicleType: form.vehicleType,
          duration: Number(form.duration),
          financialCap: Number(form.financialCap),
          kmLimit: Number(form.kmLimit),
          sellingPrice: Number(form.sellingPrice),
          isCashPayment,
          isMensualisable,
          partIds: selectedPartIds,
        },
      });
    } else {
      const dto: CreateWarrantyDto = {
        societyId,
        name: form.name.trim(),
        warrantyType: form.warrantyType,
        vehicleType: form.vehicleType,
        duration: Number(form.duration),
        financialCap: Number(form.financialCap),
        kmLimit: Number(form.kmLimit),
        sellingPrice: Number(form.sellingPrice),
        // Pas de `servicePrice` : le prix de la prestation Affinicar est résolu
        // côté API (tarif propre → parent facturant → catalogue). Le `0` qui
        // figurait ici créait des garanties à SERVICE_PRICE nul, donc des
        // contrats facturés 0 € au partenaire.
        isCashPayment,
        isMensualisable,
        isBroadcast: false,
        isActive: true,
        partIds: selectedPartIds,
      };
      createMutation.mutate(dto);
    }
  };

  if (!isOpen) return null;

  const partsLabelClass = 'tw-block tw-text-xs tw-font-semibold tw-text-gray-600 tw-mb-1';
  const errorMsgClass = 'tw-text-xs tw-text-red-600 tw-mt-1';

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-max-w-4xl tw-w-full tw-mx-4 tw-overflow-hidden tw-max-h-[90vh] tw-flex tw-flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-bg-affinicar-primary/10 tw-border-b tw-border-affinicar-primary/20 tw-px-6 tw-py-4 tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-p-2 tw-bg-affinicar-primary/20 tw-rounded-lg">
              {isEditMode
                ? <Pencil className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
                : <Plus className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
              }
            </div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0">
              {isEditMode ? 'Modifier' : 'Créer'} une {wording.singularLower} personnalisée
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-p-1 tw-rounded-lg hover:tw-bg-gray-200 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5 tw-text-gray-500" />
          </button>
        </div>

        {/* Body - scrollable */}
        <div className="tw-px-6 tw-py-5 tw-overflow-y-auto tw-flex-1">
          {/* Section: Informations */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-5">
            <div className="tw-col-span-2">
              <FormInput
                label="Nom"
                value={form.name}
                onChange={(val) => updateField('name', val)}
                placeholder={`Nom de la ${wording.singularLower}`}
                required
                maxLength={150}
                error={fieldErrors.name}
                size="sm"
              />
            </div>

            {autoWarrantyType === undefined && (
              <FormSelect
                label="Type"
                value={form.warrantyType}
                onChange={(val) => updateField('warrantyType', Number(val) as 0 | 1)}
                options={WARRANTY_TYPE_OPTIONS}
                required
                size="sm"
              />
            )}

            {filteredVehicleTypeOptions.length > 1 && (
              <FormSelect
                label="Type de véhicule"
                value={form.vehicleType}
                onChange={(val) => {
                  updateField('vehicleType', Number(val));
                  setSelectedPartIds([]);
                  setExpandedCategories(new Set());
                }}
                options={filteredVehicleTypeOptions}
                required
                size="sm"
              />
            )}

            <FormSelect
              label="Durée"
              value={form.duration}
              onChange={(val) => updateField('duration', Number(val))}
              options={filteredDurationOptions}
              placeholder="Sélectionnez"
              required
              error={fieldErrors.duration}
              size="sm"
            />

            <FormInput
              type="number"
              label="Plafond financier"
              value={form.financialCap.toString()}
              onChange={(val) => updateField('financialCap', val)}
              placeholder="3000"
              required
              suffix="€"
              error={fieldErrors.financialCap}
              size="sm"
            />

            <FormInput
              type="number"
              label="Plafond km"
              value={form.kmLimit.toString()}
              onChange={(val) => updateField('kmLimit', val)}
              placeholder="10000"
              required
              suffix="km"
              error={fieldErrors.kmLimit}
              size="sm"
            />

            <FormInput
              type="number"
              label="Prix de vente TTC"
              value={form.sellingPrice.toString()}
              onChange={(val) => {
                updateField('sellingPrice', val);
                if (Number(val) === 0) {
                  updateField('isMensualisable', false);
                }
              }}
              placeholder="0.00"
              required
              suffix="€"
              error={fieldErrors.sellingPrice}
              size="sm"
            />

            {/* Comptant seul possible sans mensualisation : rien à choisir */}
            {canMonthlyPayment && (
              <div className="tw-col-span-2">
                <div className="tw-flex tw-gap-6">
                  <Checkbox
                    checked={form.isCashPayment}
                    onChange={(checked) => {
                      updateField('isCashPayment', checked);
                      clearFieldError('paymentMode');
                    }}
                    label="Paiement comptant"
                  />
                  {Number(form.sellingPrice) > 0 && (
                    <Checkbox
                      checked={form.isMensualisable}
                      onChange={(checked) => {
                        updateField('isMensualisable', checked);
                        clearFieldError('paymentMode');
                      }}
                      label="Paiement mensuel"
                    />
                  )}
                </div>
                {fieldErrors.paymentMode && <p className={errorMsgClass}>{fieldErrors.paymentMode}</p>}
              </div>
            )}

            {/* Warning : prix total < 200€ avec mensualisation */}
            {isMensualTooLow && (
              <div className="tw-p-3 tw-bg-red-50 tw-rounded-md tw-border tw-border-red-300">
                <p className="tw-text-xs tw-text-red-800 tw-font-semibold tw-mb-0">
                  La mensualisation n'est pas disponible pour un prix total inférieur à 200€.
                  Veuillez augmenter le prix ou choisir le paiement comptant uniquement.
                </p>
              </div>
            )}

            {/* Warning pour "Mensuel uniquement" */}
            {isMensualisable && !isCashPayment && !isMensualTooLow && (
              <div className="tw-p-3 tw-bg-amber-50 tw-rounded-md tw-border tw-border-amber-300">
                <p className="tw-text-xs tw-text-amber-800 tw-font-semibold tw-mb-0">
                  ⚠️ Attention : Vous devez renseigner le prix mensuel TTC,
                  pas le prix total de la prestation
                </p>
              </div>
            )}
          </div>

          {/* Section: Couverture pièces */}
          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <label className={partsLabelClass}>Couverture de pièces * — <span className="tw-font-normal tw-text-gray-500">{selectedPartIds.length} pièce{selectedPartIds.length > 1 ? 's' : ''} sélectionnée{selectedPartIds.length > 1 ? 's' : ''}</span></label>
              {permissions.canSelectAllParts && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 tw-transition-colors"
                >
                  <CheckSquare className="tw-w-3.5 tw-h-3.5" />
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              )}
            </div>
            {isLoadingParts ? (
              <div className="tw-flex tw-justify-center tw-py-8">
                <div className="tw-animate-spin tw-h-6 tw-w-6 tw-border-4 tw-border-affinicar-primary tw-border-t-transparent tw-rounded-full" />
              </div>
            ) : (
              <div className={`tw-space-y-2 ${fieldErrors.parts ? 'tw-border tw-border-red-500 tw-rounded-lg tw-p-2' : ''}`}>
                {categories.map((category) => (
                  <CategorySection
                    key={category.catKey}
                    categoryKey={category.catKey}
                    categoryLabel={category.label}
                    parts={category.parts}
                    vehicleType={form.vehicleType === 1 ? 'collection' : form.vehicleType === 2 ? 'two_wheeler' : 'classic'}
                    selectedPartIds={selectedPartIds}
                    isExpanded={expandedCategories.has(category.catKey)}
                    canModifyDefaultParts={permissions.canModifyDefaultParts}
                    onCategoryToggle={() => handleCategoryToggle(category.catKey)}
                    onPartToggle={handlePartToggle}
                    onSelectAllPriority={handleSelectAllPriority}
                  />
                ))}
              </div>
            )}
            {fieldErrors.parts && <p className={errorMsgClass}>{fieldErrors.parts}</p>}
          </div>

          {/* Error */}
          {error && (
            <div className="tw-mt-4 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-text-sm tw-text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-flex tw-justify-end tw-gap-3">
          <button
            type="button"
            onClick={onClose}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isLoading}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-primary/90 tw-transition-colors disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2"
          >
            {mutation.isLoading && (
              <div className="tw-animate-spin tw-w-4 tw-h-4 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full" />
            )}
            {isEditMode ? 'Enregistrer' : `Créer la ${wording.singularLower}`}
          </button>
        </div>
      </div>
    </div>
  );
};
